import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updatePassword,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { auth, db } from './firebase';
import { User } from '../types/user';
import { addLocalRecentTrailer } from './localStorageService';

// Liste des emails administrateurs
export const ADMIN_EMAILS = [
  'admin@cinedelux.com',
  'ytbrelaxmusic@gmail.com',
  'matking073@hotmail.com'
];

// Fonction de connexion
export const signIn = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
    
    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }
    
    return userDoc.data() as User;
  } catch (error: any) {
    console.error('Erreur de connexion:', error);
    throw error;
  }
};

export const signUp = async (email: string, password: string, displayName?: string): Promise<User> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    const newUser: User = {
      uid: userCredential.user.uid,
      email: email,
      displayName: displayName || email.split('@')[0],
      username: email.split('@')[0],
      photoURL: null,
      role: ADMIN_EMAILS.includes(email) ? 'admin' : 'user',
      recentTrailers: [],
      watchlist: { movies: [], tvShows: [] },
      favorites: [],
      history: [],
      settings: {
        darkMode: true,
        notifications: true,
        language: 'fr',
        privacy: 'public',
        autoplayTrailers: true
      }
    };
    
    await setDoc(doc(db, 'users', userCredential.user.uid), newUser);
    return newUser;
  } catch (error) {
    console.error('Erreur lors de l\'inscription:', error);
    throw error;
  }
};

export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Erreur lors de la déconnexion:', error);
    throw error;
  }
};

export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Erreur lors de la réinitialisation du mot de passe:', error);
    throw error;
  }
};

export const changePassword = async (newPassword: string): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Aucun utilisateur connecté');
    }
    await updatePassword(user, newPassword);
  } catch (error) {
    console.error('Erreur lors du changement de mot de passe:', error);
    throw error;
  }
};

export const addRecentTrailer = async (
  userId: string,
  mediaId: number,
  mediaType: 'movie' | 'tv',
  mediaTitle: string,
  thumbnail: string,
  releaseDate: string
): Promise<User | void> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (!userData) {
      console.error('User data not found');
      return;
    }
    
    // Créer la nouvelle bande-annonce
    const newTrailer = {
      mediaId,
      mediaType,
      mediaTitle,
      thumbnail,
      releaseDate,
      viewedAt: new Date().toISOString()
    };

    // Sauvegarder dans le localStorage
    addLocalRecentTrailer(newTrailer);

    // Récupérer les bandes-annonces existantes ou initialiser un tableau vide
    let recentTrailers = userData.recentTrailers || [];
    
    // Vérifier si la bande-annonce existe déjà
    const existingIndex = recentTrailers.findIndex(
      (trailer) => trailer.mediaId === mediaId && trailer.mediaType === mediaType
    );

    if (existingIndex !== -1) {
      // Si elle existe, la supprimer pour la remettre au début
      recentTrailers.splice(existingIndex, 1);
    }

    // Ajouter la nouvelle bande-annonce au début
    recentTrailers.unshift(newTrailer);

    // Garder seulement les 10 dernières bandes-annonces
    recentTrailers = recentTrailers.slice(0, 10);

    // Mettre à jour le document avec le nouveau tableau
    await updateDoc(userRef, {
      recentTrailers
    });

    // Retourner les données utilisateur mises à jour
    return {
      ...userData,
      recentTrailers
    } as User;
  } catch (error) {
    console.error('Erreur lors de l\'ajout de la bande-annonce récente:', error);
    throw error;
  }
};

export const updateUserAvatar = async (userId: string, avatarUrl: string): Promise<void> => {
  try {
    await updateProfile(auth.currentUser!, {
      photoURL: avatarUrl
    });
    await updateDoc(doc(db, 'users', userId), {
      photoURL: avatarUrl
    });
  } catch (error) {
    console.error('Error updating avatar:', error);
    throw error;
  }
};
